/* eslint-disable react/no-unknown-property */
import { extend } from '@pixi/react'
import { useAudio } from '@providers'
import { AnimatedSprite as PixiAnimatedSprite, PointData, Texture } from 'pixi.js'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { randIntFromInterval } from '@ds'
import { useDesignPixel, webApp } from '@shared'

import type { StarSize } from './Star'

extend({
  AnimatedSprite: PixiAnimatedSprite,
})

const MAX_DELAY = 1000
const DEFAULT_ANIMATION_SPEED = 0.5
const LOOP_END_RATIO = 0.79

const SIZE_REM_MAP: Record<StarSize, number> = {
  xs: 32,
  s: 42,
  m: 56,
  l: 64,
  xl: 64,
  xxl: 204,
}

export interface StarPixiHandle {
  triggerCollect: () => void
}

interface StarPixiProps {
  textures: Texture[]
  x: number
  y: number
  size?: StarSize
  delay?: number
  onDisappear?: () => void
  disabled?: boolean
  onClick?: () => void
  shouldAnimate?: boolean
  isVisible?: boolean
  isActive?: boolean
  anchor?: number | PointData
}

export const StarPixi = forwardRef<StarPixiHandle, StarPixiProps>(function StarPixi({
  textures,
  x,
  anchor = 0,
  y,
  size = 's',
  delay = randIntFromInterval(0, MAX_DELAY),
  onDisappear = () => {},
  onClick = () => {},
  disabled = false,
  shouldAnimate = true,
  isVisible = true,
}, ref) {
  const spriteRef = useRef<PixiAnimatedSprite | null>(null)
  const [isCompleted, setIsCompleted] = useState(false)
  const startTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isInitializedRef = useRef(false)
  const { playStar } = useAudio()
  const { designPixelValue } = useDesignPixel()
  const loopEndFrame = useMemo(() => {
    if (textures.length <= 1) return 0
    const lastFrame = textures.length - 1
    const target = Math.floor(lastFrame * LOOP_END_RATIO)
    return Math.min(lastFrame - 1, Math.max(1, target))
  }, [textures.length])

  const sizePx = useMemo(() => {
    const remSize = SIZE_REM_MAP[size] ?? SIZE_REM_MAP.s
    return remSize * designPixelValue
  }, [designPixelValue, size])

  const clearStartTimer = () => {
    if (startTimerRef.current) {
      clearTimeout(startTimerRef.current)
      startTimerRef.current = null
    }
  }

  const startPlayback = useCallback(() => {
    if (!spriteRef.current || !shouldAnimate || isCompleted) {
      return
    }
    spriteRef.current.visible = false;
    clearStartTimer()
    startTimerRef.current = setTimeout(() => {
      if (!spriteRef.current) return
      spriteRef.current.loop = false;
      spriteRef.current.animationSpeed = DEFAULT_ANIMATION_SPEED
      spriteRef.current.onFrameChange = () => {
        const current = spriteRef.current?.currentFrame ?? 0
        if (current >= loopEndFrame) {
          spriteRef.current?.gotoAndPlay(0)
        }
      }
      spriteRef.current.gotoAndPlay(0)
      spriteRef.current.visible = true;

    }, delay)
  }, [delay, isCompleted, loopEndFrame, shouldAnimate])

  useEffect(() => {
    const sprite = spriteRef.current
    if (!sprite || textures.length === 0) return

    if (!isVisible) {
      clearStartTimer()
      sprite.stop()
      return
    }

    if (!shouldAnimate) {
      clearStartTimer()
      sprite.gotoAndStop(0)
      return
    }

    if (!isInitializedRef.current) {
      isInitializedRef.current = true
      startPlayback()
    } else if (!sprite.playing && !isCompleted) {
      startPlayback()
    }
  }, [textures.length, isCompleted, isVisible, shouldAnimate, startPlayback])

  useEffect(() => {
    return () => {
      clearStartTimer()
      const sprite = spriteRef.current
      if (sprite) {
        sprite.onComplete = undefined
        sprite.onFrameChange = undefined
      }
    }
  }, [])

  const handleClick = useCallback(() => {
    if (disabled || isCompleted) {
      return
    }

    const sprite = spriteRef.current
    if (!sprite) return

    onClick()
    setIsCompleted(true)
    playStar()
    webApp.HapticFeedback?.impactOccurred('soft')

    sprite.loop = false
    sprite.animationSpeed = DEFAULT_ANIMATION_SPEED
    sprite.onFrameChange = () => {
      const current = spriteRef.current?.currentFrame ?? 0
      const lastFrame = textures.length - 1
      if (current >= lastFrame) {
        spriteRef.current?.stop()
        onDisappear()
      }
    }
    sprite.gotoAndPlay(loopEndFrame)
  }, [
    disabled,
    isCompleted,
    loopEndFrame,
    onClick,
    onDisappear,
    playStar,
    textures.length,
  ])

  const setSpriteRef = useCallback((sprite: PixiAnimatedSprite | null) => {
    const wasInitialized = isInitializedRef.current
    spriteRef.current = sprite
    if (sprite && !wasInitialized) {
      sprite.gotoAndStop(0)
    }
  }, [])

  useImperativeHandle(ref, () => ({ triggerCollect: handleClick }), [handleClick])

  if (textures.length === 0) {
    return null
  }

  return (
    <pixiAnimatedSprite
      ref={setSpriteRef}
      textures={textures}
      anchor={anchor}
      x={x}
      y={y}
      width={sizePx}
      height={sizePx}
      animationSpeed={DEFAULT_ANIMATION_SPEED}
      visible={isVisible}
      eventMode={disabled ? 'none' : 'static'}
      cursor={disabled ? 'default' : 'pointer'}
      onTap={handleClick}
    />
  )
})
